// formConfig.js

/**
 * Section Weights Configuration
 *
 * These weights determine how much each section contributes to the overall form completion.
 * The weighting system is based on several factors:
 * - Legal/compliance requirements
 * - Operational necessity
 * - Time sensitivity of the information
 * - Impact on business processes
 *
 * Weight Scale:
 * 2.0: Critical sections required for basic operations
 * 1.5: High-priority sections with legal/compliance implications
 * 1.2: Important operational information
 * 0.8: Supplementary information that can be updated later
 */
export const SECTION_WEIGHTS = {
    staff_info: 2.0,        // Highest priority - Core personal information needed for HR systems
    identification_doc: 1.5, // High priority - Legal documents required for compliance
    employment: 1.5,        // High priority - Essential for payroll and access management
    contact: 1.2,           // Medium-high priority - Critical for emergency situations
    equipment: 0.8,         // Lower priority - Can be updated after onboarding
    training: 0.8,          // Lower priority - Can be completed progressively
    medical: 1.2            // Medium-high priority - Important for health and safety compliance
};

/**
 * Calculate total weight for normalization
 * This sum is used to convert weighted progress into a percentage
 * For example, if total weight is 9.0, each section's contribution
 * would be (section_weight / 9.0) * section_completion
 */
export const TOTAL_WEIGHT = Object.values(SECTION_WEIGHTS)
    .reduce((sum, weight) => sum + weight, 0);

/**
 * Required Fields Configuration
 *
 * Defines the mandatory fields for each section and their relative importance.
 * Each field has a weight that indicates its importance within its section:
 * 1.0: Essential fields that must be completed
 * 0.8: Important fields that should be completed but aren't blocking
 * 0.5: Optional fields that are recommended but not required
 *
 * This granular weighting allows for more accurate progress calculation
 * and better guidance for users on what to complete first.
 */
export const REQUIRED_FIELDS = {
    staff_info: {
        gender: 1.0,
        nationality: 1.0,
        index_number: 0.8,
        un_id: 0.8,
        un_id_expiration_date: 0.8,
        staff_status: 1.0,
        dependants: 0.8,
        date_of_birth: 1.0
    },
    identification_doc: {
        national_passport: 0.5,
        national_passport_expiry_date: 0.5,
        drivers_license: 0.5,
        drivers_license_expiry_date: 0.5,
        diplomatic_card: 0.5,
        diplomatic_card_expiry_date: 0.5,
        national_id_card: 0.5,
        national_id_card_expiry_date: 0.5,
        unlp: 0.5,
        unlp_expiry_date: 0.5,
        country_of_issue: 0.5,
        issue_date: 0.5,
        expiration_date: 0.5
    },
    employment: {
        function: 1.0,          // Essential - Job role definition
        section: 1.0,           // Essential - Organizational placement
        unit: 1.0,              // Essential - Team assignment
        type_of_contract: 1.0

    },
    contact: {
        call_sign: 1.0,         // Essential - Internal identification
        primary_emergency_contact_name: 1.0,    // Essential - Safety requirement
        primary_emergency_contact_number: 1.0,  // Essential - Safety requirement
        secondary_emergency_contact_name: 0.5   // Recommended but optional
    },
    equipment: {
        category: 1.0,          // Essential - Asset classification
        make: 1.0,              // Essential - Asset identification
        model: 1.0              // Essential - Asset specification
    },
    training: {
        training_name: 1.0,     // Essential - Course identification
        training_status: 1.0,   // Essential - Completion tracking
        year_completed: 1.0,    // Essential - Timeline tracking
        training_mode: 0.5      // Optional - Delivery method
    },
    medical: {
        blood_type: 1.0,    // Essential - Required for medical identification
        medication: 0.8,    // Important but not blocking
        allergy: 0.8        // Important but not blocking
    }
};

/**
 * Validation Rules
 *
 * Defines the validation functions for different types of form fields.
 * Each rule returns true if the field meets the validation criteria.
 * These rules are used in conjunction with field weights to calculate
 * completion status.
 */
export const VALIDATION_RULES = {
    // Basic required field validation
    required: (value) => {
        // Checks for non-empty strings after trimming whitespace
        // Also accepts 0 as a valid value for numeric fields
        return Boolean(value && value.toString().trim());
    },

    // Date field validation
    date: (value) => {
        const date = new Date(value);
        // Ensures the date is valid and not Invalid Date
        return !isNaN(date) && date instanceof Date;
    },

    // Phone number validation (basic international format)
    phone: (value) => {
        // Accepts numbers with optional + prefix
        // Minimum 8 digits, allows spaces and hyphens
        return /^\+?[\d\s-]{8,}$/.test(value);
    },

    // Email validation
    email: (value) => {
        // Basic email format validation
        // Requires @ symbol and domain with at least one period
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
};

/**
 * Initial Form State
 *
 * Defines the default structure and values for the form.
 * This ensures consistent data structure throughout the application
 * and provides appropriate defaults for new form instances.
 */
export const INITIAL_FORM_STATE = {
    staff_info: {
        gender: '',
        nationality: '',
        index_number: '',
        un_id: '',
        un_id_expiration_date: '',
        staff_status: '',
        dependants: 0,
        date_of_birth: ''
    },
    identification_doc: {
        national_passport: '',
        national_passport_expiry_date: '',
        drivers_license: '',
        drivers_license_expiry_date: '',
        diplomatic_card: '',
        diplomatic_card_expiry_date: '',
        national_id_card: '',
        national_id_card_expiry_date: '',
        unlp: '',
        unlp_expiry_date: '',
        country_of_issue: '',
        issue_date: '',
        expiration_date: ''
    },
    employment: {},
    contact: {
        call_sign: '',
        primary_emergency_contact_name: '',
        primary_emergency_contact_number: ''
    },
    equipment: {},
    training: [{
        training_name: '',
        training_status: '',
        training_mode: '',
        year_completed: ''
    }],
    medical: []
};

/**
 * Section Display Configuration
 *
 * Defines how each section should be displayed in the UI.
 * This includes icons and human-readable labels for better user experience.
 */
export const SECTION_LABELS = {
    staff_info: 'Staff Information',
    identification_doc: 'Identification Documents',
    employment: 'Employment Information',
    contact: 'Contact Information',
    equipment: 'Equipment Information',
    training: 'Training Information',
    medical: 'Medical Information'
};

/**
 * Icon mappings for visual representation of sections
 * These correspond to FontAwesome icon names
 */
export const SECTION_ICONS = {
    staff_info: 'user',
    identification_doc: 'id-card',
    employment: 'briefcase',
    contact: 'address-book',
    equipment: 'laptop',
    training: 'book',
    medical: 'heartbeat'
};